import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getChessCoachStatus } from '../../api/playing';
import ChessCoachPanel from './ChessCoachPanel';

const DEFAULT_W = 360;
const DEFAULT_H = 480;
const MIN_W = 300;
const MIN_H = 320;
const PAD = 12;

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function maxSize() {
  return {
    w: Math.max(MIN_W, window.innerWidth - PAD * 2),
    h: Math.max(MIN_H, window.innerHeight - PAD * 2),
  };
}

function defaultWindowPos(boardEl, size) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = size?.w ?? DEFAULT_W;
  const h = size?.h ?? DEFAULT_H;
  if (!boardEl) {
    return {
      x: Math.max(PAD, vw - w - 24),
      y: Math.max(PAD, Math.round(vh * 0.18)),
    };
  }
  const b = boardEl.getBoundingClientRect();
  const x = clamp(b.right + 20, PAD, vw - w - PAD);
  const y = clamp(b.top + 8, PAD, vh - h - PAD);
  return { x, y };
}

/**
 * Game-scoped ChessDesk Coach: opens from the board, stays tied to this gameId,
 * and draws a visible tether from the chessboard to a draggable/resizable chat window.
 */
export default function GameBoardCoach({
  boardRef,
  gameId,
  focusPly = null,
}) {
  const { t } = useTranslation();
  const [coachLive, setCoachLive] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getChessCoachStatus()
      .then((s) => {
        if (!cancelled) setCoachLive(!!(s?.enabled && s?.configured));
      })
      .catch(() => {
        if (!cancelled) setCoachLive(false);
      });
    return () => { cancelled = true; };
  }, []);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [tether, setTether] = useState(null);
  /** Which board edge hosts the pin + string: follows the chat window. */
  const [boardSide, setBoardSide] = useState('right');
  const windowRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const posRef = useRef(pos);
  const sizeRef = useRef(size);
  posRef.current = pos;
  sizeRef.current = size;

  const updateTether = useCallback(() => {
    const board = boardRef?.current;
    const win = windowRef.current;
    if (!board || !win || !open) {
      setTether(null);
      return;
    }
    const b = board.getBoundingClientRect();
    const w = win.getBoundingClientRect();
    const winCx = w.left + w.width / 2;
    const boardCx = b.left + b.width / 2;
    // Chat left of board → attach to left edge; otherwise right. Never cross mid-board.
    const chatOnLeft = winCx < boardCx;
    setBoardSide(chatOnLeft ? 'left' : 'right');

    const x1 = chatOnLeft ? b.left - 10 : b.right + 10;
    const y1 = b.top + b.height * 0.42;
    // Window edge facing the board
    const x2 = chatOnLeft ? w.right - 6 : w.left + 6;
    const y2 = w.top + 28;
    // Bend the curve in the gap outside the board (not through the squares)
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    const gap = Math.abs(x2 - x1);
    const away = chatOnLeft ? -1 : 1;
    const cx = midX + away * Math.min(36, gap * 0.18);
    const cy = midY - Math.min(56, Math.abs(y2 - y1) * 0.25 + 16);
    setTether({ x1, y1, x2, y2, cx, cy });
  }, [boardRef, open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateTether();
    const onMove = (e) => {
      // Ignore scrolls inside the chat (messages list) — those must not re-render
      // the tether or fight the user's reading scroll.
      if (e?.target && windowRef.current?.contains(e.target)) return;
      updateTether();
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, pos, size, updateTether]);

  const openChat = useCallback(() => {
    setPos(defaultWindowPos(boardRef?.current, sizeRef.current));
    setOpen(true);
  }, [boardRef]);

  const closeChat = useCallback(() => {
    setOpen(false);
    setTether(null);
  }, []);

  const onDragStart = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    if (resizeRef.current) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = posRef.current;
    dragRef.current = { startX, startY, origin };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onDragMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { startX, startY, origin } = dragRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { w, h } = sizeRef.current;
    const next = {
      x: clamp(origin.x + (e.clientX - startX), PAD, vw - w - PAD),
      y: clamp(origin.y + (e.clientY - startY), PAD, vh - Math.min(h, 120)),
    };
    setPos(next);
  }, []);

  const onDragEnd = useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const onResizeStart = useCallback((edge) => (e) => {
    if (e.button != null && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = null;
    resizeRef.current = {
      edge,
      startX: e.clientX,
      startY: e.clientY,
      originPos: { ...posRef.current },
      originSize: { ...sizeRef.current },
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onResizeMove = useCallback((e) => {
    const r = resizeRef.current;
    if (!r) return;
    const { edge, startX, startY, originPos, originSize } = r;
    const caps = maxSize();
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    let nextX = originPos.x;
    let nextY = originPos.y;
    let nextW = originSize.w;
    let nextH = originSize.h;

    if (edge.includes('e')) {
      nextW = clamp(originSize.w + dx, MIN_W, Math.min(caps.w, window.innerWidth - originPos.x - PAD));
    }
    if (edge.includes('s')) {
      nextH = clamp(originSize.h + dy, MIN_H, Math.min(caps.h, window.innerHeight - originPos.y - PAD));
    }
    if (edge.includes('w')) {
      const maxW = Math.min(caps.w, originPos.x + originSize.w - PAD);
      nextW = clamp(originSize.w - dx, MIN_W, maxW);
      nextX = originPos.x + (originSize.w - nextW);
    }
    if (edge.includes('n')) {
      const maxH = Math.min(caps.h, originPos.y + originSize.h - PAD);
      nextH = clamp(originSize.h - dy, MIN_H, maxH);
      nextY = originPos.y + (originSize.h - nextH);
    }

    setPos({ x: nextX, y: nextY });
    setSize({ w: nextW, h: nextH });
  }, []);

  const onResizeEnd = useCallback((e) => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    // Close when leaving this game
    setOpen(false);
    setTether(null);
  }, [gameId]);

  if (gameId == null) return null;
  if (coachLive !== true) return null;

  return (
    <>
      <button
        type="button"
        className={`playing-game-coach-pin playing-game-coach-pin--${open ? boardSide : 'right'}`}
        onClick={() => (open ? closeChat() : openChat())}
        aria-expanded={open}
        aria-controls="playing-game-coach-window"
        title={t('playing.coach.gamePinTitle', {
          defaultValue: 'Ask AI Coach about this game',
        })}
      >
        <span className="playing-game-coach-pin__dot" aria-hidden />
        <span className="playing-game-coach-pin__label">
          {open
            ? t('playing.coach.hideGameChat', { defaultValue: 'Hide Ask AI Coach' })
            : t('playing.coach.openGameChat', { defaultValue: 'Ask AI Coach' })}
        </span>
      </button>

      {open && tether && (
        <svg
          className="playing-game-coach-tether"
          width="100%"
          height="100%"
          aria-hidden
        >
          <defs>
            <linearGradient id="gameCoachTetherGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0f766e" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#0369a1" stopOpacity="0.85" />
            </linearGradient>
            <filter id="gameCoachTetherGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            d={`M ${tether.x1} ${tether.y1} Q ${tether.cx} ${tether.cy} ${tether.x2} ${tether.y2}`}
            fill="none"
            stroke="url(#gameCoachTetherGrad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            filter="url(#gameCoachTetherGlow)"
          />
          <circle cx={tether.x1} cy={tether.y1} r="5" fill="#0f766e" stroke="#fff" strokeWidth="2" />
          <circle cx={tether.x2} cy={tether.y2} r="4" fill="#0369a1" stroke="#fff" strokeWidth="2" />
        </svg>
      )}

      {open && (
        <div
          id="playing-game-coach-window"
          ref={windowRef}
          className="playing-game-coach-window"
          style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
          role="dialog"
          aria-modal="false"
          aria-label={t('playing.coach.gameTitle', {
            defaultValue: 'Ask AI Coach',
          })}
        >
          <header
            className="playing-game-coach-window__header"
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
          >
            <div className="playing-game-coach-window__header-main">
              <p className="playing-game-coach-window__title">
                {t('playing.coach.gameTitle', { defaultValue: 'Ask AI Coach' })}
              </p>
              <p className="playing-game-coach-window__sub">
                {t('playing.coach.gameHint', {
                  defaultValue: 'Ask anything · drag header to move · drag any edge to resize',
                })}
              </p>
            </div>
            <button
              type="button"
              className="playing-game-coach-window__close"
              onClick={closeChat}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={t('playing.coach.minimize', { defaultValue: 'Minimize chat' })}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14" strokeLinecap="round" />
              </svg>
            </button>
          </header>
          <div className="playing-game-coach-window__body">
            <ChessCoachPanel
              embedded
              gameId={gameId}
              focusPly={focusPly}
            />
          </div>
          {[
            { edge: 'n', label: 'Resize top' },
            { edge: 's', label: 'Resize bottom' },
            { edge: 'e', label: 'Resize right' },
            { edge: 'w', label: 'Resize left' },
            { edge: 'ne', label: 'Resize top-right' },
            { edge: 'nw', label: 'Resize top-left' },
            { edge: 'se', label: 'Resize bottom-right' },
            { edge: 'sw', label: 'Resize bottom-left' },
          ].map(({ edge, label }) => (
            <div
              key={edge}
              className={`playing-game-coach-window__resize playing-game-coach-window__resize--${edge}`}
              onPointerDown={onResizeStart(edge)}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeEnd}
              onPointerCancel={onResizeEnd}
              role="separator"
              aria-label={t('playing.coach.resizeEdge', { defaultValue: label, edge: label })}
              title={t('playing.coach.resize', { defaultValue: 'Resize chat window' })}
            />
          ))}
        </div>
      )}
    </>
  );
}
